/**
 * Jurídico: ocorrências de pirataria (ADR 0065, E3). Suspeita, violação confirmada, resolvida
 * e suspeita descartada são situações distintas. Providências são só registradas: o sistema não
 * envia notificações, denúncias nem pedidos de remoção.
 */
import { z } from 'zod';

export const PIRACY_PLATFORM_KINDS = [
  'marketplace',
  'social_media',
  'website',
  'file_sharing',
  'messaging',
  'physical_store',
  'other',
] as const;
export const PIRACY_VIOLATION_TYPES = [
  'counterfeit_product',
  'unauthorized_file_distribution',
  'unauthorized_reproduction',
  'trademark_misuse',
  'unauthorized_sale',
  'other',
] as const;
export const PIRACY_STATUSES = ['suspected', 'confirmed', 'resolved', 'dismissed'] as const;
export const PIRACY_OUTCOMES = [
  'removed',
  'agreement',
  'legal_action',
  'no_action',
  'other',
] as const;
export const PIRACY_ACTION_KINDS = [
  'notice',
  'takedown_request',
  'platform_report',
  'legal_referral',
  'monitoring',
  'other',
] as const;
export const PIRACY_ACTION_RESULTS = ['pending', 'succeeded', 'failed', 'no_response'] as const;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z
  .uuid()
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));
const required = (message: string) => z.string().regex(/^\d{4}-\d{2}-\d{2}$/, message);

export const legalPiracyCaseInput = z
  .object({
    legalEntityId: z.uuid(),
    workId: id,
    ipAssetId: id,
    affectedDescription: text(1000),
    platformKind: z.enum(PIRACY_PLATFORM_KINDS),
    platformName: z.string().trim().min(1, 'Informe a plataforma').max(200),
    url: z
      .string()
      .trim()
      .max(2000)
      .refine((v) => v === '' || /^https?:\/\/\S+$/i.test(v), 'Use um endereço que comece com http')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    violationType: z.enum(PIRACY_VIOLATION_TYPES),
    identifiedOn: required('Informe a data em que foi identificada'),
    description: text(10000),
    status: z.enum(PIRACY_STATUSES).default('suspected'),
    confirmedOn: day,
    closedOn: day,
    outcome: z.enum(PIRACY_OUTCOMES).nullable().optional(),
    outcomeNote: text(4000),
    ownerId: id,
    confidentiality: z.enum(['restricted', 'confidential']).default('restricted'),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (!v.workId && !v.ipAssetId && !v.affectedDescription) {
      issue('affectedDescription', 'Indique a obra, a marca ou descreva o que foi violado');
    }
    if (v.status === 'confirmed' && !v.confirmedOn) {
      issue('confirmedOn', 'Informe a data da confirmação');
    }
    if (v.status === 'resolved') {
      if (!v.closedOn) issue('closedOn', 'Informe a data da solução');
      if (!v.outcome) issue('outcome', 'Informe o resultado');
    }
    if (v.status === 'dismissed') {
      if (!v.closedOn) issue('closedOn', 'Informe a data');
      if (!v.outcomeNote) issue('outcomeNote', 'Explique por que a suspeita foi descartada');
    }
  });
export type LegalPiracyCaseInput = z.infer<typeof legalPiracyCaseInput>;

export const legalPiracyActionInput = z.object({
  kind: z.enum(PIRACY_ACTION_KINDS),
  doneOn: required('Informe a data'),
  channel: text(200),
  protocol: text(200),
  result: z.enum(PIRACY_ACTION_RESULTS).default('pending'),
  lawsuitId: id,
  note: text(4000),
});
export type LegalPiracyActionInput = z.infer<typeof legalPiracyActionInput>;
